/**
 * 日志级别
 */
export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
  NONE = 4
}

/**
 * 日志配置
 */
interface LoggerConfig {
  level: LogLevel
  prefix: string
  enableTimestamp: boolean
  enableStackTrace: boolean
}

/**
 * 日志工具类
 */
class Logger {
  private config: LoggerConfig = {
    level: LogLevel.INFO,
    prefix: '[TwyfordSentry]',
    enableTimestamp: true,
    enableStackTrace: false
  }

  /**
   * 设置日志级别
   */
  setLevel(level: LogLevel): void {
    this.config.level = level
  }

  /**
   * 设置日志前缀
   */
  setPrefix(prefix: string): void {
    this.config.prefix = prefix
  }

  /**
   * 启用/禁用时间戳
   */
  setTimestamp(enable: boolean): void {
    this.config.enableTimestamp = enable
  }

  /**
   * 启用/禁用堆栈跟踪
   */
  setStackTrace(enable: boolean): void {
    this.config.enableStackTrace = enable
  }

  /**
   * 格式化日志消息
   */
  private format(level: string, ...args: any[]): any[] {
    const parts: any[] = []

    // 添加前缀
    if (this.config.prefix) {
      parts.push(this.config.prefix)
    }

    // 添加时间戳
    if (this.config.enableTimestamp) {
      const timestamp = new Date().toISOString()
      parts.push(`[${timestamp}]`)
    }

    // 添加级别
    parts.push(`[${level}]`)

    // 添加消息
    parts.push(...args)

    return parts
  }

  /**
   * 获取堆栈信息
   */
  private getStackTrace(): string {
    const stack = new Error().stack
    if (!stack) return ''

    const lines = stack.split('\n')
    // 跳过 Error、getStackTrace、log 方法本身的堆栈
    return lines.slice(4).join('\n')
  }

  /**
   * Debug 级别日志
   */
  debug(...args: any[]): void {
    if (this.config.level <= LogLevel.DEBUG) {
      console.debug(...this.format('DEBUG', ...args))

      if (this.config.enableStackTrace) {
        console.debug(this.getStackTrace())
      }
    }
  }

  /**
   * Info 级别日志
   */
  info(...args: any[]): void {
    if (this.config.level <= LogLevel.INFO) {
      console.info(...this.format('INFO', ...args))
    }
  }

  /**
   * Warn 级别日志
   */
  warn(...args: any[]): void {
    if (this.config.level <= LogLevel.WARN) {
      console.warn(...this.format('WARN', ...args))

      if (this.config.enableStackTrace) {
        console.warn(this.getStackTrace())
      }
    }
  }

  /**
   * Error 级别日志
   */
  error(...args: any[]): void {
    if (this.config.level <= LogLevel.ERROR) {
      console.error(...this.format('ERROR', ...args))

      if (this.config.enableStackTrace) {
        console.error(this.getStackTrace())
      }
    }
  }

  /**
   * 分组日志开始
   */
  group(label: string): void {
    if (this.config.level <= LogLevel.INFO) {
      console.group(...this.format('GROUP', label))
    }
  }

  /**
   * 分组日志结束
   */
  groupEnd(): void {
    if (this.config.level <= LogLevel.INFO) {
      console.groupEnd()
    }
  }

  /**
   * 表格日志
   */
  table(data: any): void {
    if (this.config.level <= LogLevel.DEBUG) {
      console.log(...this.format('TABLE'))
      console.table(data)
    }
  }

  /**
   * 计时开始
   */
  time(label: string): void {
    if (this.config.level <= LogLevel.DEBUG) {
      console.time(`${this.config.prefix} ${label}`)
    }
  }

  /**
   * 计时结束
   */
  timeEnd(label: string): void {
    if (this.config.level <= LogLevel.DEBUG) {
      console.timeEnd(`${this.config.prefix} ${label}`)
    }
  }

  /**
   * 断言日志
   */
  assert(condition: boolean, ...args: any[]): void {
    if (this.config.level <= LogLevel.ERROR) {
      console.assert(condition, ...this.format('ASSERT', ...args))
    }
  }

  /**
   * 统计日志
   */
  count(label: string): void {
    if (this.config.level <= LogLevel.DEBUG) {
      console.count(`${this.config.prefix} ${label}`)
    }
  }

  /**
   * 重置统计
   */
  countReset(label: string): void {
    if (this.config.level <= LogLevel.DEBUG) {
      console.countReset(`${this.config.prefix} ${label}`)
    }
  }

  /**
   * 追踪日志
   */
  trace(...args: any[]): void {
    if (this.config.level <= LogLevel.DEBUG) {
      console.trace(...this.format('TRACE', ...args))
    }
  }
}

// 导出单例
export const logger = new Logger()

// 根据环境自动设置日志级别
if (typeof process !== 'undefined' && process.env) {
  const env = process.env.NODE_ENV
  if (env === 'production') {
    logger.setLevel(LogLevel.WARN)
  } else if (env === 'test') {
    logger.setLevel(LogLevel.ERROR)
  } else {
    logger.setLevel(LogLevel.DEBUG)
  }
}

// 在浏览器环境中根据 localStorage 设置日志级别
if (typeof window !== 'undefined' && window.localStorage) {
  const debugMode = localStorage.getItem('SENTRY_DEBUG')
  if (debugMode === 'true') {
    logger.setLevel(LogLevel.DEBUG)
    logger.setStackTrace(true)
  }
}
